<template>
  <div class="agency-page-preview">
    <template v-if="!hideHeader">
      <h3>PortalBuilder Vorschau</h3>
      <v-divider></v-divider>
    </template>
    <div v-if="loading" class="preview-loading">
      <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
    </div>
    <template v-else>
      <div v-if="pages.length > 0" class="preview-layout">
        <div class="preview-sidebar">
          <div
            v-for="(page, index) in pages"
            :key="page.pageDefinitionId"
            class="sidebar-item"
            :class="{ active: selectedIndex === index }"
            @click="selectPage(index)"
          >
            <span class="sidebar-item-name">{{ page.pageName }}</span>
            <span
              class="sidebar-item-status"
              :class="page.liveCustomerCount > 0 ? 'status-live' : 'status-draft'"
            >
              {{ page.liveCustomerCount > 0 ? `Live (${page.liveCustomerCount})` : 'Entwurf' }}
            </span>
          </div>
        </div>
        <div class="preview-main">
          <div v-if="selectedPage" class="preview-detail">
            <v-card variant="outlined" class="preview-card">
              <v-img
                v-if="selectedPage.previewS3Key"
                :src="getPreviewUrl(selectedPage.previewS3Key)"
                alt="Preview"
                class="preview-image"
                @click="openFullscreenDialog"
              ></v-img>
              <div v-else class="preview-placeholder">
                <v-icon size="48">mdi-image-off-outline</v-icon>
                <span>Keine Vorschau</span>
              </div>
            </v-card>
            <div class="page-meta-section">
              <div v-if="selectedPage.createdAt || selectedPage.lastChange" class="page-meta-row">
                <span class="page-meta-label">Erstellt:</span>
                <span>{{ formatDate(selectedPage.createdAt || selectedPage.lastChange) }}</span>
              </div>
              <div v-if="selectedPage.firstPublishedAt" class="page-meta-row">
                <span class="page-meta-label">Veröffentlicht:</span>
                <span>{{ formatDate(selectedPage.firstPublishedAt) }}</span>
              </div>
            </div>
            <div class="deployment-section">
              <h4>Veröffentlichungsstatus</h4>
              <div v-if="selectedPage.deployments.length > 0" class="deployment-list">
                <div
                  v-for="d in selectedPage.deployments"
                  :key="d.customerId"
                  class="deployment-item"
                  @click="openCustomerDialog(d.customerId)"
                >
                  <span class="deployment-customer">{{ d.customerName }}</span>
                  <v-chip
                    :color="d.published ? 'success' : 'default'"
                    size="small"
                    variant="tonal"
                  >
                    {{ d.published ? 'Live' : 'Entwurf' }}
                  </v-chip>
                </div>
              </div>
              <p v-else class="deployment-empty">Nicht an Kunden ausgerollt</p>
            </div>
          </div>
        </div>
      </div>
      <p v-else class="preview-empty">Keine Seiten vorhanden</p>

      <v-dialog v-model="fullscreenDialog" max-width="90%" @click:outside="fullscreenDialog = false">
        <v-card>
          <div class="fullscreen-image-wrapper">
            <v-img
              v-if="selectedPage?.previewS3Key"
              :src="getPreviewUrl(selectedPage.previewS3Key)"
              alt="Fullscreen Preview"
              class="fullscreen-image"
            ></v-img>
            <div v-if="selectedPage" class="fullscreen-info-panel">
              <div class="fullscreen-info-row">
                <strong>{{ selectedPage.pageName }}</strong>
              </div>
              <div class="fullscreen-info-row">
                <v-chip
                  :color="selectedPage.liveCustomerCount > 0 ? 'success' : 'default'"
                  size="small"
                  variant="tonal"
                >
                  {{ selectedPage.liveCustomerCount > 0 ? `Live (${selectedPage.liveCustomerCount})` : 'Entwurf' }}
                </v-chip>
              </div>
              <div v-if="selectedPage.createdAt || selectedPage.lastChange" class="fullscreen-info-row fullscreen-info-meta">
                Erstellt: {{ formatDate(selectedPage.createdAt || selectedPage.lastChange) }}
              </div>
              <div v-if="selectedPage.firstPublishedAt" class="fullscreen-info-row fullscreen-info-meta">
                Veröffentlicht: {{ formatDate(selectedPage.firstPublishedAt) }}
              </div>
            </div>
          </div>
          <div v-if="pages.length > 1" class="fullscreen-navigation">
            <v-btn
              @click="selectPage(Math.max(0, selectedIndex - 1))"
              :disabled="selectedIndex === 0"
              icon="mdi-arrow-left"
              variant="tonal"
            ></v-btn>
            <v-btn
              @click="selectPage(Math.min(pages.length - 1, selectedIndex + 1))"
              :disabled="selectedIndex === pages.length - 1"
              icon="mdi-arrow-right"
              variant="tonal"
            ></v-btn>
          </div>
        </v-card>
      </v-dialog>
    </template>
  </div>
</template>

<script>
import apiClient from "@/plugins/axios";

export default {
  props: {
    agencyId: {
      type: String,
      required: true,
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      pages: [],
      selectedIndex: 0,
      fullscreenDialog: false,
      loading: false,
    };
  },
  computed: {
    selectedPage() {
      return this.pages[this.selectedIndex] || null;
    },
  },
  watch: {
    agencyId: {
      handler(newId) {
        if (newId) {
          this.fetchPages();
        } else {
          this.pages = [];
          this.selectedIndex = 0;
        }
      },
      immediate: true,
    },
  },
  methods: {
    async fetchPages() {
      if (!this.agencyId) return;
      this.loading = true;
      try {
        const response = await apiClient.get("/getters/getAgencyPagePreviews", {
          params: { agencyId: this.agencyId },
        });
        this.pages = response.data;
        this.selectedIndex = this.pages.length > 0 ? 0 : 0;
      } catch (error) {
        console.error("Failed to fetch page previews:", error);
        this.pages = [];
      } finally {
        this.loading = false;
      }
    },
    getPreviewUrl(s3key) {
      return `https://d3jzzvbvsu2f3f.cloudfront.net/${s3key}`;
    },
    selectPage(index) {
      this.selectedIndex = index;
    },
    openCustomerDialog(customerId) {
      this.$store.dispatch("agency/setDialogReturnRoute", this.$route.fullPath);
      this.$store.commit("customer/setCustomerDialog", true);
      this.$store.commit("agency/resetAgencyDialog");
      this.$router.push({ name: "customer", params: { customerId: String(customerId) } });
    },
    openFullscreenDialog() {
      this.fullscreenDialog = true;
    },
    formatDate(date) {
      if (!date) return "—";
      return new Date(date).toLocaleDateString("de-DE", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
.agency-page-preview {
  padding: 16px 0;
}

.preview-loading {
  display: flex;
  justify-content: center;
  padding: 24px;
}

.preview-layout {
  display: flex;
  gap: 16px;
  min-height: 220px;
}

.preview-sidebar {
  width: 180px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: auto;
}

.sidebar-item {
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid rgb(230, 230, 230);
  transition: background-color 0.15s ease;
}

.sidebar-item:hover {
  background-color: #f5f5f5;
}

.sidebar-item.active {
  background-color: #e3f2fd;
  border-color: #2196f3;
}

.sidebar-item-name {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  margin-bottom: 2px;
}

.sidebar-item-status {
  font-size: 0.75rem;
}

.sidebar-item-status.status-live {
  color: #2e7d32;
}

.sidebar-item-status.status-draft {
  color: #757575;
}

.preview-main {
  flex: 1;
  min-width: 0;
}

.preview-detail {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.preview-card {
  overflow: hidden;
  border-radius: 8px;
}

.preview-image {
  height: 180px;
  object-fit: cover;
  cursor: pointer;
}

.preview-image:hover {
  opacity: 0.95;
}

.preview-placeholder {
  height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: rgba(0, 0, 0, 0.5);
  font-size: 0.875rem;
}

.deployment-section h4 {
  font-size: 0.9rem;
  margin: 0 0 8px 0;
  color: rgba(0, 0, 0, 0.7);
}

.deployment-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.deployment-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background-color: #f5f5f5;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.deployment-item:hover {
  background-color: #eeeeee;
}

.deployment-customer {
  font-size: 0.875rem;
}

.deployment-empty {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.875rem;
  margin: 0;
}

.preview-empty {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.875rem;
  margin-top: 8px;
}

.fullscreen-image-wrapper {
  position: relative;
}

.fullscreen-image {
  height: 80vh;
  object-fit: contain;
}

.fullscreen-info-panel {
  position: absolute;
  bottom: 16px;
  left: 16px;
  background: rgba(0, 0, 0, 0.75);
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  max-width: 280px;
}

.fullscreen-info-row {
  margin-bottom: 4px;
}

.fullscreen-info-row:last-child {
  margin-bottom: 0;
}

.fullscreen-info-row.fullscreen-info-meta {
  font-size: 0.8rem;
  opacity: 0.9;
}

.fullscreen-info-row strong {
  font-size: 0.95rem;
}

.page-meta-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;
}

.page-meta-row {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.7);
}

.page-meta-label {
  margin-right: 8px;
  font-weight: 500;
}

.fullscreen-navigation {
  margin: 16px 0;
  display: flex;
  justify-content: center;
  gap: 8px;
}
</style>
